/**
 * CollabExternalInbox – lister alle eksterne henvendelser sendt via invitasjons-lenker.
 * Vises som egen tab i /skaper/samarbeid?tab=external-inbox.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, CheckCircle2, Circle, Calendar as CalIcon, CheckCheck } from "lucide-react";
import api from "../../lib/api";

export default function CollabExternalInbox() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("unhandled"); // unhandled | handled | all
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "unhandled") params.handled = 0;
      else if (filter === "handled") params.handled = 1;
      const r = await api.get("/collab/external-messages", { params });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke laste innboks.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const FILTERS = [
    { k: "unhandled", label: "Uhåndtert" },
    { k: "handled", label: "Håndtert" },
    { k: "all", label: "Alle" },
  ];

  return (
    <div data-testid="ext-inbox">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {FILTERS.map(({ k, label }) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 font-meta text-xs border transition-colors ${
              filter === k
                ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10"
                : "border-white/15 text-[var(--cream-soft)] hover:border-[var(--cream)]"
            }`}
            data-testid={`ext-inbox-filter-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="panel-tight p-12 text-center text-[var(--cream-soft)] font-fraunces italic">
          Laster…
        </div>
      ) : !items || items.length === 0 ? (
        <div className="panel-tight p-12 text-center" data-testid="ext-inbox-empty">
          <Inbox className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-3" />
          <div className="font-fraunces italic text-lg text-[var(--cream-soft)]">
            {filter === "unhandled"
              ? "Ingen uhåndterte henvendelser."
              : filter === "handled"
              ? "Ingen håndterte henvendelser enda."
              : "Ingen henvendelser mottatt enda."}
          </div>
          <p className="text-xs text-[var(--cream-faint)] mt-2">
            Del en invitasjons-lenke for å motta forespørsler fra eksterne.
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="ext-inbox-list">
          {items.map((m) => {
            const platLabel = (m.external_platform || "").toUpperCase();
            const dateStr = m.slot_date
              ? new Date(m.slot_date).toLocaleDateString("nb-NO", { weekday: "short", day: "numeric", month: "short" })
              : null;
            return (
              <li key={m.id}
                className={`panel-tight p-4 ${!m.read ? "border-[var(--magenta)]/40 bg-[var(--magenta)]/5" : ""}`}
                data-testid={`ext-inbox-item-${m.id}`}>
                <Link to={`/skaper/samarbeid/henvendelse/${m.id}`} className="block group">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-fraunces italic text-lg text-[var(--cream)] group-hover:text-[var(--magenta)] transition-colors">
                          @{m.external_handle}
                        </span>
                        <span className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)]">
                          {platLabel}
                        </span>
                      </div>
                      {m.slot_date && m.slot_type && (
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span className="font-meta text-[10px] px-2 py-0.5 border border-[var(--magenta)]/60 text-[var(--magenta)] uppercase tracking-wider">
                            {m.slot_type.toUpperCase()}
                          </span>
                          <span className="font-manrope text-xs text-[var(--cream-soft)] inline-flex items-center gap-1">
                            <CalIcon className="h-3 w-3" /> {dateStr}
                          </span>
                        </div>
                      )}
                      {(m.posted_by_creator || m.posted_by_partner) && (
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {m.posted_by_creator && m.posted_by_partner ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-meta tracking-wider text-[#7BBF6E]">
                              <CheckCheck className="h-3 w-3" /> POSTET AV BEGGE
                            </span>
                          ) : m.posted_by_creator ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-meta tracking-wider text-[var(--gold)]">
                              <CheckCheck className="h-3 w-3" /> POSTET FRA DEG
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-meta tracking-wider text-[var(--gold)]">
                              <CheckCheck className="h-3 w-3" /> POSTET AV MOTPARTEN
                            </span>
                          )}
                        </div>
                      )}
                      <p className="text-sm text-[var(--cream-soft)] mt-2 line-clamp-2">
                        {m.message}
                      </p>
                      <div className="text-[10px] font-meta text-[var(--cream-faint)] mt-1">
                        {new Date(m.created_at).toLocaleString("nb-NO")}
                      </div>
                    </div>
                    <div className="shrink-0">
                      {m.handled ? (
                        <CheckCircle2 className="h-5 w-5 text-[var(--gold)]" title="Håndtert" />
                      ) : (
                        <Circle className="h-5 w-5 text-[var(--cream-faint)]" title="Uhåndtert" />
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
